import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';
import { AuthCompany, AuthUser } from 'src/models';
import { AuthState } from 'src/app/store/auth/auth.state';
import { GuideProgress, GuideProgressState, resumableGuideProgress } from 'src/app/store/guide-progress/guide-progress.state';
import { painpointGuideKeyForVisit, routePath } from 'src/app/util/painpoint-landing.util';
import { AmplitudeService, amplitudeUserId } from '../amplitude.service';

/** When this app instance started. Progress saved after it belongs to a guide the SDK is still running. */
const PAGE_LOADED_AT = Date.now();

interface GuideToOpen {
  guideKey: string;
  step?: number;
  /** Amplitude user id, so a different owner in the same tab is not deduplicated away. */
  ownerId: string | null;
}

/**
 * Picks the guide to open on this page: a guide a reload interrupted on this
 * page, else the focus-area guide when an owner lands on that card's page.
 *
 * Only progress saved before this page load is resumed. Progress saved in this
 * load means the SDK is already showing that guide; asking for it again would
 * call `refresh`, which can close or restart it.
 */
export function guideToOpen(
  url: string,
  company: AuthCompany | undefined,
  user: AuthUser | undefined,
  progress: GuideProgress | null,
  now = Date.now(),
  pageLoadedAt = PAGE_LOADED_AT,
): GuideToOpen | null {
  const ownerId = company?.id && user?.id ? amplitudeUserId(user.id, company.id) : null;
  const resumable = resumableGuideProgress(progress, ownerId, now);
  if (resumable && resumable.savedAt < pageLoadedAt && resumable.path === routePath(url)) {
    return { guideKey: resumable.guideKey, step: resumable.step, ownerId };
  }

  const guideKey = painpointGuideKeyForVisit({
    painpoint: company?.companySettings?.custom?.painpoint,
    url,
    trackingMode: company?.companySettings?.trackingMode,
    accessLevel: company?.role,
  });

  if (!guideKey) {
    return null;
  }

  const shownInThisLoad = resumable?.guideKey === guideKey && resumable.savedAt >= pageLoadedAt;
  return shownInThisLoad ? null : { guideKey, ownerId };
}

/**
 * Refreshes Amplitude guides for the page an owner lands on.
 * Audience targeting decides which guide to show.
 * One listener covers every report route.
 */
@Injectable({
  providedIn: 'root',
})
export class PainpointGuideTrigger {
  /** When this app instance started; see `guideToOpen`. */
  pageLoadedAt = PAGE_LOADED_AT;

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly amplitude: AmplitudeService,
  ) {}

  start(): void {
    const url$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url),
    );

    combineLatest([
      url$,
      this.store.select(AuthState.company),
      this.store.select(AuthState.user),
      this.store.select(GuideProgressState.progress),
    ]).pipe(
      map(([url, company, user, progress]) => guideToOpen(url, company, user, progress, Date.now(), this.pageLoadedAt)),
      filter((guide): guide is GuideToOpen => guide !== null),
      // After the null filter, so going back to the landing page does not reopen the guide.
      distinctUntilChanged((a, b) => a.guideKey === b.guideKey && a.step === b.step && a.ownerId === b.ownerId),
      switchMap(guide => this.amplitude.showGuideWhenReady(guide.guideKey, guide.step)),
    ).subscribe();
  }
}
